import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Shuffle } from 'lucide-react';
import api from '@/lib/api';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

export default function OwnerMotivationalPhrases() {
    const [phrases, setPhrases] = useState([]);
    const [gym, setGym] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ phrase: '', is_active: true, sort_order: 0 });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [updatingRandom, setUpdatingRandom] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [phrasesRes, gymRes] = await Promise.all([
                api.get('/owner/motivational-phrases'),
                api.get('/owner/gym'),
            ]);
            setPhrases(phrasesRes.data.data || []);
            setGym(gymRes.data.data);
        } catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openCreate = () => {
        setEditing(null);
        setForm({ phrase: '', is_active: true, sort_order: phrases.length });
        setError('');
        setDialogOpen(true);
    };

    const openEdit = (phrase) => {
        setEditing(phrase);
        setForm({ phrase: phrase.phrase, is_active: phrase.is_active, sort_order: phrase.sort_order });
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            if (editing) {
                await api.put(`/owner/motivational-phrases/${editing.id}`, form);
            } else {
                await api.post('/owner/motivational-phrases', form);
            }
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Eliminar esta frase?')) return;
        try {
            await api.delete(`/owner/motivational-phrases/${id}`);
            fetchData();
        } catch {}
    };

    const toggleRandom = async (value) => {
        if (!gym) return;
        setUpdatingRandom(true);
        try {
            await api.put('/owner/gym', { motivational_phrases_random: value });
            setGym(prev => prev ? { ...prev, motivational_phrases_random: value } : prev);
        } catch {
            setGym(prev => prev ? { ...prev, motivational_phrases_random: !value } : prev);
        } finally { setUpdatingRandom(false); }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Frases Motivacionales</h1>
                <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Nueva Frase</Button>
            </div>

            {/* Random order toggle */}
            <Card className="bg-[#0a0a0a] border-[#3a3a3f] mb-6">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                <Shuffle className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-white uppercase tracking-wide">Orden aleatorio</p>
                                <p className="text-xs text-white/60">
                                    {gym?.motivational_phrases_random
                                        ? 'Las frases se muestran en orden aleatorio'
                                        : 'Las frases se muestran según el orden configurado'}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={gym?.motivational_phrases_random ?? true}
                            onCheckedChange={toggleRandom}
                            disabled={updatingRandom || !gym}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-[#0a0a0a] border-[#3a3a3f] overflow-hidden">
                <CardContent className="p-3 sm:p-6">
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : phrases.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No hay frases configuradas.</p>
                    ) : (
                        <div className="grid gap-3">
                            {phrases.map((p) => (
                                <div key={p.id} className="flex items-center justify-between p-2 sm:p-4 rounded-lg border border-[#3a3a3f] bg-[#0a0a0a] min-w-0">
                                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                        <span className="hidden sm:block text-sm text-white/40 font-mono w-6 text-center shrink-0">{p.sort_order}</span>
                                        <div className="min-w-0">
                                            <p className={`text-sm truncate ${p.is_active ? 'text-white' : 'text-white/40'}`}>&ldquo;{p.phrase}&rdquo;</p>
                                            <p className="text-xs text-white/40">{p.is_active ? 'Activa' : 'Inactiva'}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} className="text-white hover:bg-white/5 h-8 w-8"><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} className="text-white hover:bg-white/5 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">{editing ? 'Editar Frase' : 'Nueva Frase'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <label className="text-xs text-white/60 uppercase tracking-wider">Frase</label>
                            <Input value={form.phrase} onChange={e => setForm({ ...form, phrase: e.target.value })} required maxLength={500} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs text-white/60 uppercase tracking-wider">Orden</label>
                                <Input type="number" min="0" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white" />
                            </div>
                            <div className="flex items-center gap-2 pt-6">
                                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded border-[#3a3a3f] bg-[#0a0a0a] text-white" />
                                <span className="text-sm text-white">Activa</span>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
